import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMemo, useState, useEffect } from 'react';
import { Award, MapPin, Clock, ChevronRight, Star, Leaf } from 'lucide-react';
import PromoSection from '../components/PromoSection';
import Reveal, { RevealList, RevealItem } from '../components/Reveal';
import '../assets/styles/home.css';

const FEATURED_SERVICES = [
  {
    title: 'Signature Facials',
    description:
      'Tailored Dermalogica and Joyce Blok treatments — from a 30-minute express to advanced nano-fusion. Your skin, your ritual.',
    duration: 'From 30 min',
    price: 'From $70',
    category: 'Skin',
  },
  {
    title: 'Nails & Spa',
    description:
      'CND manicures, Magpie gel nails, luxury spa pedicures — plus quick express options when you are short on time.',
    duration: 'From 15 min',
    price: 'From $35',
    category: 'Nails',
  },
  {
    title: 'Eye Treatments',
    description:
      'Refectocil brow and lash tinting and shaping — precise definition that lasts 4–6 weeks, no mascara required.',
    duration: 'From 15 min',
    price: 'From $25',
    category: 'Eyes',
  },
  {
    title: 'Massage',
    description:
      'Aromatherapy, Swedish and targeted foot or head massage — pick your escape, full body or focused relief.',
    duration: 'From 20 min',
    price: 'From $50',
    category: 'Massage',
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Viktoria is absolutely amazing. I've been coming for years and always leave feeling so confident and genuinely cared for.",
    name: 'Sarah M.',
    treatment: 'Gel Nails & Facial',
  },
  {
    quote:
      'The most relaxing and professional experience. Her attention to detail is second to none. I recommend Viktoria to everyone.',
    name: 'Jo W.',
    treatment: 'Dermalogica Facial',
  },
];

const TRUST_ITEMS = [
  { icon: Award, label: '20+ Years Experience' },
  { icon: Leaf,  label: 'Dermalogica Stockist' },
  { icon: MapPin, label: 'Nelson, New Zealand' },
  { icon: Clock, label: 'Mon – Fri by Appointment' },
];

const ANIMATED_WORDS = ['beautiful', 'confident', 'radiant', 'glowing', 'stunning'];
const EASE = [0.25, 0.46, 0.45, 0.94];

function Home() {
  const navigate = useNavigate();
  const [wordIndex, setWordIndex] = useState(0);
  const words = useMemo(() => ANIMATED_WORDS, []);

  useEffect(() => {
    const id = setTimeout(
      () => setWordIndex((n) => (n === words.length - 1 ? 0 : n + 1)),
      2400
    );
    return () => clearTimeout(id);
  }, [wordIndex, words]);

  return (
    <div className="home">
      <PromoSection />

      {/* ── Hero ── */}
      <section className="hero-section">
        <div className="hero-inner">
          <motion.div
            className="hero-text"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <p className="hero-eyebrow">Nelson, New Zealand</p>
            <h1 className="hero-heading">
              Feel{' '}
              <span className="hero-animated-wrap" aria-live="polite" aria-atomic="true">
                {words.map((word, i) => (
                  <motion.span
                    key={word}
                    className="hero-animated-word"
                    initial={{ opacity: 0, y: 36 }}
                    animate={
                      wordIndex === i
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: wordIndex > i ? -36 : 36 }
                    }
                    transition={{ type: 'spring', stiffness: 90, damping: 20 }}
                  >
                    {word}
                  </motion.span>
                ))}
              </span>
            </h1>
            <motion.p
              className="hero-desc"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.18, ease: EASE }}
            >
              With over 20 years of experience, I deliver premium beauty
              treatments — Dermalogica facials, gel nails, massage and waxing —
              in a calm, welcoming Nelson studio.
            </motion.p>
            <motion.div
              className="hero-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32, ease: EASE }}
            >
              <button className="btn-primary" onClick={() => navigate('/book')}>
                Book an Appointment
              </button>
              <button className="btn-secondary" onClick={() => navigate('/price')}>
                View Services <ChevronRight size={16} strokeWidth={2} />
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            className="hero-image-wrap"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.75, delay: 0.1, ease: EASE }}
          >
            <img
              src="/images/reducedPics/Nails-home.webp"
              alt="Gel nail treatment"
              loading="eager"
            />
          </motion.div>
        </div>
      </section>

      {/* ── Trust Bar ── */}
      <Reveal variant="fade" duration={0.5}>
        <section className="trust-bar" aria-label="Credentials">
          <div className="trust-inner">
            {TRUST_ITEMS.map(({ icon: Icon, label }, i) => (
              <div key={label} className="trust-item-wrap">
                {i > 0 && <div className="trust-divider" aria-hidden="true" />}
                <div className="trust-item">
                  <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
                  <span>{label}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      {/* ── Services Preview ── */}
      <section className="services-section">
        <div className="page-container">
          <Reveal variant="up">
            <div className="section-header">
              <p className="section-eyebrow">What I Offer</p>
              <h2 className="section-heading">Treatments for Every You</h2>
            </div>
          </Reveal>
          <RevealList className="services-grid" stagger={0.13}>
            {FEATURED_SERVICES.map((s) => (
              <RevealItem key={s.title}>
                <article className="service-card">
                  <span className="service-badge">{s.category}</span>
                  <h3 className="service-card-title">{s.title}</h3>
                  <p className="service-card-desc">{s.description}</p>
                  <div className="service-card-meta">
                    <span className="service-card-duration">{s.duration}</span>
                    <span className="service-card-price">{s.price}</span>
                  </div>
                  <button
                    className="service-card-cta"
                    onClick={() => navigate('/book')}
                    aria-label={`Book ${s.title}`}
                  >
                    Book Now
                  </button>
                </article>
              </RevealItem>
            ))}
          </RevealList>
          <Reveal variant="up" delay={0.15}>
            <div className="services-view-all">
              <button className="btn-secondary" onClick={() => navigate('/price')}>
                View Full Price List <ChevronRight size={16} strokeWidth={2} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="testimonials-section">
        <div className="page-container">
          <Reveal variant="up">
            <div className="section-header">
              <p className="section-eyebrow">Client Love</p>
              <h2 className="section-heading">What My Clients Say</h2>
            </div>
          </Reveal>
          <RevealList className="testimonials-grid" stagger={0.14}>
            {TESTIMONIALS.map((t, i) => (
              <RevealItem key={i} variant="scale">
                <article className="testimonial-card">
                  <div className="testimonial-stars" aria-label="5 out of 5 stars">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} size={14} fill="var(--clr-accent)" color="var(--clr-accent)" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="testimonial-quote">"{t.quote}"</blockquote>
                  <footer className="testimonial-author">
                    <p className="testimonial-name">{t.name}</p>
                    <p className="testimonial-treatment">{t.treatment}</p>
                  </footer>
                </article>
              </RevealItem>
            ))}
          </RevealList>
        </div>
      </section>

      {/* ── About ── */}
      <section className="about-section">
        <div className="page-container">
          <div className="about-inner">
            <Reveal variant="right">
              <div className="about-image-wrap">
                <img
                  src="/images/reducedPics/viktoria-headshot.webp"
                  alt="Viktoria, your beauty therapist"
                  className="about-img"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal variant="left">
              <div className="about-text">
                <p className="section-eyebrow">Meet Your Therapist</p>
                <h2 className="section-heading">Meet Viktoria</h2>
                <p>
                  With over 20 years as a qualified beauty therapist and nail technician, I'm
                  passionate about making you feel genuinely cared for — not just pampered.
                  A beauty treatment is a moment of calm in your day, and I'm honoured to be
                  part of it.
                </p>
                <p>
                  I'm a proud Nelson local, a mum of three, and a believer that beauty should
                  feel personal. I pride myself on listening, adapting to your needs, and
                  creating an experience that leaves you walking out feeling like yourself —
                  only better.
                </p>
                <button
                  className="btn-primary"
                  onClick={() => navigate('/book')}
                  style={{ marginTop: '1.75rem' }}
                >
                  Book with Viktoria
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <Reveal variant="scale" duration={0.65}>
        <section className="cta-banner">
          <div className="page-container">
            <h2 className="cta-banner-heading">Ready to feel beautiful?</h2>
            <p className="cta-banner-sub">
              Book online in minutes — I'll confirm your appointment shortly.
            </p>
            <button className="btn-cta-large" onClick={() => navigate('/book')}>
              Book Your Appointment
            </button>
          </div>
        </section>
      </Reveal>

      {/* ── Social ── */}
      <Reveal variant="up">
        <section className="social-section">
          <div className="page-container">
            <p className="social-label">Follow along</p>
            <a
              href="https://www.instagram.com/beauty_byviktoria/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-handle"
            >
              @beauty_byviktoria
            </a>
            <div className="social-links">
              <a href="https://www.instagram.com/beauty_byviktoria/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                Instagram
              </a>
              <a href="https://www.facebook.com/Viktoriahouseofbeauty" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                Facebook
              </a>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}

export default Home;
